import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { RESPONSIVE } from '@src/constants/responsive';
import { memo, type ReactElement, useEffect, useMemo, useState } from 'react';

const BASE_PAGE_WIDTH = 595;
const BASE_PAGE_HEIGHT = 842;
const PAGE_ASPECT_RATIO = `${BASE_PAGE_WIDTH} / ${BASE_PAGE_HEIGHT}`;

const useWindowSize = (): { width: number | null; height: number | null } => {
  const [size, setSize] = useState<{
    width: number | null;
    height: number | null;
  }>(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : null,
    height: typeof window !== 'undefined' ? window.innerHeight : null,
  }));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () =>
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return size;
};

/**
 * メモ化されたスケルトンコンポーネント
 *
 * PDF読み込み中のローディング表示を提供します。
 *
 * @returns {ReactElement} PDFプレースホルダー
 *
 * @example
 * ```tsx
 * <SkeletonLoader />
 * ```
 */
export const SkeletonLoader = memo((): ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const isDark = theme.palette.mode === 'dark';

  const fallbackPageWidth = useMemo(() => {
    if (isMobile) {
      return 'min(92vw, 440px)';
    }
    if (isTablet) {
      return 'min(82vw, 600px)';
    }
    return 'min(70vw, 760px)';
  }, [isMobile, isTablet]);

  const skeletonDimensions = useMemo(() => {
    if (!windowWidth || !windowHeight) {
      return null;
    }

    const breakpoints = RESPONSIVE.BREAKPOINTS;
    const pdfSizing = RESPONSIVE.PDF_SIZING;
    const margin = RESPONSIVE.PDF_CONTAINER_MARGIN;

    let containerWidth: number;
    let containerHeight: number;

    if (windowWidth >= breakpoints.DESKTOP_LARGE) {
      containerWidth = windowWidth * pdfSizing.DESKTOP_LARGE.WIDTH_RATIO;
      containerHeight = windowHeight * pdfSizing.DESKTOP_LARGE.HEIGHT_RATIO;
    } else if (windowWidth >= breakpoints.DESKTOP_MEDIUM) {
      containerWidth = windowWidth * pdfSizing.DESKTOP_MEDIUM.WIDTH_RATIO;
      containerHeight = windowHeight * pdfSizing.DESKTOP_MEDIUM.HEIGHT_RATIO;
    } else if (windowWidth >= breakpoints.TABLET) {
      containerWidth = windowWidth * pdfSizing.TABLET.WIDTH_RATIO;
      containerHeight = windowHeight * pdfSizing.TABLET.HEIGHT_RATIO;
    } else {
      containerWidth = windowWidth * pdfSizing.MOBILE.WIDTH_RATIO;
      containerHeight = windowHeight * pdfSizing.MOBILE.HEIGHT_RATIO;
    }

    const scaleX = (containerWidth - margin) / BASE_PAGE_WIDTH;
    const scaleY = (containerHeight - margin) / BASE_PAGE_HEIGHT;

    let scale: number;
    if (windowWidth >= breakpoints.TABLET) {
      scale = Math.max(
        pdfSizing.DESKTOP_LARGE.SCALE_MIN,
        Math.min(scaleX, pdfSizing.DESKTOP_LARGE.SCALE_MAX)
      );
    } else {
      const constrainedScale = Math.min(scaleX, scaleY);
      scale = Math.max(
        pdfSizing.MOBILE.SCALE_MIN,
        Math.min(constrainedScale, pdfSizing.MOBILE.SCALE_MAX)
      );
    }

    return {
      width: Math.floor(BASE_PAGE_WIDTH * scale),
    };
  }, [windowHeight, windowWidth]);

  const colors = useMemo(
    () => ({
      pageBackground: isDark ? 'rgba(15, 23, 42, 0.92)' : '#ffffff',
      pageBorder: isDark
        ? '1px solid rgba(148, 163, 184, 0.28)'
        : '1px solid rgba(15, 23, 42, 0.2)',
      pageShadow: isDark ? '0 18px 36px rgba(0, 0, 0, 0.55)' : '0 14px 32px rgba(15, 23, 42, 0.14)',
      overlay: isDark ? 'rgba(148, 163, 184, 0.08)' : 'rgba(148, 163, 184, 0.12)',
      line: isDark ? 'rgba(226, 232, 240, 0.18)' : 'rgba(148, 163, 184, 0.28)',
    }),
    [isDark]
  );

  const insetPadding = useMemo(() => {
    if (isMobile) {
      return theme.spacing(2);
    }
    if (isTablet) {
      return theme.spacing(3);
    }
    return theme.spacing(4);
  }, [isMobile, isTablet, theme]);

  const lineHeight = useMemo(() => (isMobile ? 12 : 14), [isMobile]);

  const textLines = useMemo(
    () =>
      [
        { width: '58%', height: lineHeight + 6 },
        { width: '36%', height: lineHeight + 2 },
        { width: '90%' },
        { width: '86%' },
        { width: '93%' },
        { width: '72%' },
        { width: '68%' },
        { width: '80%' },
        { width: '88%' },
        { width: '54%' },
      ].map((line, index) => (
        <Skeleton
          key={`${line.width}-${index}`}
          variant="rectangular"
          animation="wave"
          sx={{
            width: line.width,
            height: line.height ?? lineHeight,
            borderRadius: 0.5,
            bgcolor: colors.line,
          }}
        />
      )),
    [colors.line, lineHeight]
  );

  return (
    <Box
      role="presentation"
      aria-hidden
      sx={{
        width: skeletonDimensions ? `${skeletonDimensions.width}px` : fallbackPageWidth,
        aspectRatio: PAGE_ASPECT_RATIO,
        maxWidth: '100%',
        margin: '0 auto',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: 2,
          border: colors.pageBorder,
          overflow: 'hidden',
          backgroundColor: colors.pageBackground,
          boxShadow: colors.pageShadow,
        }}
      >
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: colors.overlay,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: insetPadding,
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 1 : 1.5,
            pointerEvents: 'none',
          }}
        >
          {textLines}
        </Box>
      </Box>
    </Box>
  );
});

SkeletonLoader.displayName = 'SkeletonLoader';
